import {
    ScrollShadow,
    Tooltip,
    type Selection,
    type TooltipProps
} from '@heroui/react';
import { Icon } from '@iconify/react';
import type { Key } from '@react-types/shared';
import clsx from 'clsx';
import { first, isString } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, type To } from 'react-router';
import { Listbox, ListboxItem, ListboxSection } from '../listbox';
import type { Collapseable } from '../types';
import { isSidebarGroupItems } from './SidebarMenusUtils';

// Enhanced type definitions following HeroUI patterns
export type BaseMenuItemType = {
    key: Key;
    to: To;
    title?: string;
    icon?: string;
    tooltip?: TooltipProps;
    access?: string;
    hideInMenu?: boolean;
    description?: string;
    badge?: string;
    badgePlacement?: 'left' | 'right' | 'top' | 'bottom';
    shortcut?: string;
};

export type SidebarMenuItem = Omit<React.ComponentProps<typeof ListboxItem>, "children" | "href"> & BaseMenuItemType;
export type SidebarMenuItems = SidebarMenuItem[];
export type SidebarMenuGroupItem = Omit<React.ComponentProps<typeof ListboxSection>, "items" | "children"> & {
    key: Key;
    items: SidebarMenuItems;
    description?: string;
};
export type SidebarMenuGroupItems = SidebarMenuGroupItem[];
export type SidebarMenuItemsType = SidebarMenuItems | SidebarMenuGroupItems;

export interface SidebarMenusProps extends Omit<React.ComponentProps<typeof Listbox>, "children" | "items" | "onAction" | "topContent" | "bottomContent" | "onSelectionChange">, Collapseable {
    items: SidebarMenuItemsType;
    defaultSelectionKey?: Key;
    topContent?: ((isCollapsed?: boolean) => React.ReactNode) | React.ReactNode;
    bottomContent?: ((isCollapsed?: boolean) => React.ReactNode) | React.ReactNode;
    onSelectionChange?: (keys: Selection) => void;
    showTooltips?: boolean;
    enableAnimations?: boolean;
    enableBadges?: boolean;
    enableShortcuts?: boolean;
}



export const SidebarMenus: React.FC<SidebarMenusProps> = React.memo(({
    defaultSelectionKey,
    items,
    isCollapsed,
    topContent,
    bottomContent,
    onSelectionChange,
    showTooltips = true,
    enableAnimations = true,
    enableBadges = true,
    enableShortcuts = true,
    ...rawProps
}) => {

    const location = useLocation();
    const navigate = useNavigate();

    // Optimized first item key calculation
    const firstItemKey = useMemo((): Key => {
        if (isSidebarGroupItems(items)) {
            return first(items)?.items?.[0]?.key as Key || "";
        }
        return first(items)?.key as Key || "";
    }, [items]);

    // Optimized initial state
    const [selectedKeys, setSelectedKeys] = useState<Selection>(() => {
        return new Set(defaultSelectionKey ? [defaultSelectionKey] : [firstItemKey]);
    });

    const listItems = useCallback((items: SidebarMenuItems) => items.map(({
        key,
        icon,
        title,
        description,
        badge,
        badgePlacement,
        shortcut,
        hideInMenu,
        tooltip,
        ...itemProps
    }) => {

        const shouldShowTooltip = showTooltips && isCollapsed;

        return (
            <ListboxItem
                key={key}
                startContent={
                    shouldShowTooltip ? (
                        <Tooltip
                            placement="right"
                            delay={300}
                            closeDelay={100}
                            {...tooltip}
                        >
                            <Icon
                                width={24}
                                height={24}
                                className={clsx(
                                    "outline-0",
                                    enableAnimations && "transition-all duration-300 ease-out"
                                )}
                                icon={icon || "solar:question-circle-line-duotone"}
                            />
                        </Tooltip>
                    ) : (
                        <Icon
                            width={24}
                            height={24}
                            className={clsx(
                                "flex-shrink-0 outline-0",
                                enableAnimations && "transition-all duration-300 ease-out"
                            )}
                            icon={icon || "solar:question-circle-line-duotone"}
                        />
                    )
                }
                badge={enableBadges && badge ? badge : undefined}
                badgePlacement={badgePlacement || "right"}
                endContent={
                    !isCollapsed && enableShortcuts && shortcut && (
                        <kbd className={clsx(
                            "inline-flex items-center px-2 py-1 text-xs font-mono text-default-600",
                            "bg-default-100 border border-default-300 rounded",
                            enableAnimations && "transition-all duration-200"
                        )}>
                            {shortcut}
                        </kbd>
                    )
                }
                textValue={`${key}`}
                className={clsx({ 'hidden': hideInMenu }, "")}
                classNames={{
                    base: clsx(
                        "min-h-11 rounded-lg h-auto group",
                        "hover:bg-default-100 focus:bg-default-100",
                        enableAnimations && "transition-all duration-300 ease-out",
                        isCollapsed
                            ? "!w-11 justify-center items-center px-0 mx-auto"
                            : "transition-none w-full px-3"
                    ),
                    wrapper: clsx(
                        "flex w-full",
                        enableAnimations && "transition-all duration-300 ease-out",
                        {
                            "hidden": isCollapsed
                        }
                    ),
                    title: clsx(
                        "font-medium text-default-700 group-hover:text-default-900",
                        "data-[selected=true]:text-primary-600 data-[selected=true]:font-semibold",
                        enableAnimations && "transition-all duration-300 ease-out",
                        isCollapsed
                            ? "w-0 opacity-0 scale-0 pointer-events-none"
                            : "w-auto opacity-100 scale-100"
                    ),
                    description: clsx(
                        "text-xs text-default-500",
                        enableAnimations && "transition-all duration-300 ease-out",
                        isCollapsed ? "opacity-0" : "opacity-100"
                    ),
                }}
                title={isCollapsed ? undefined : title}
                description={isCollapsed ? undefined : description}
                {...itemProps}
            />
        );
    }), [isCollapsed, showTooltips, enableAnimations, enableBadges, enableShortcuts]);

    const findObjectByKey = useCallback((items: SidebarMenuItemsType, key: Key): SidebarMenuItem | null => {
        if (isSidebarGroupItems(items)) {
            for (const group of items) {
                const result = findObjectByKey(group.items, key);
                if (result) return result;
            }
        } else {
            for (const item of items) {
                if (item.key === key) {
                    return item;
                }
            }
        }
        return null;
    }, []);

    const findKeyByPath = useCallback((items: SidebarMenuItemsType, pathname: string): Key | null => {
        if (isSidebarGroupItems(items)) {
            for (const group of items) {
                const result = findKeyByPath(group.items, pathname);
                if (result) return result;
            }
        } else {
            for (const item of items) {
                const itemPath = typeof item.to === 'string' ? item.to : item.to.pathname;
                if (itemPath === pathname && item.key) {
                    return item.key as Key;
                }
            }
        }
        return null;
    }, []);

    // Enhanced effect with better performance
    useEffect(() => {
        const currentKey = findKeyByPath(items, location.pathname);
        if (currentKey) {
            setSelectedKeys(prevKeys => {
                const prevArray = Array.from(prevKeys);
                if (prevArray.length !== 1 || prevArray[0] !== currentKey) {
                    return new Set([currentKey]);
                }
                return prevKeys;
            });
        }
    }, [location.pathname, findKeyByPath, items]);

    const handleSelectionChange = useCallback((keys: Selection) => {
        const key = first(Array.from(keys));
        if (key && isString(key)) {
            const findItem = findObjectByKey(items, key);
            if (findItem) {
                setSelectedKeys(keys);
                navigate(findItem.to);
            }
        }

        onSelectionChange?.(keys);
    }, [items, navigate, onSelectionChange, findObjectByKey]);

    // Enhanced keyboard support
    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
        if (enableShortcuts) {
            // Example: Handle Escape to clear selection
            if (event.key === 'Escape') {
                event.preventDefault();
                // Could implement special behavior here
            }
        }
    }, [enableShortcuts]);

    return (
        <ScrollShadow
            hideScrollBar
            className={clsx(
                "h-full max-h-full",
                enableAnimations && "transition-all duration-300 ease-out"
            )}
            orientation="vertical"
        >
            {typeof topContent === "function" ? topContent(isCollapsed) : topContent}
            <Listbox
                as="nav"
                aria-label="Main navigation"
                hideSelectedIcon
                disallowEmptySelection
                selectedKeys={selectedKeys}
                className="list-none"
                classNames={{
                    list: clsx(
                        "items-center gap-2 p-2",
                        enableAnimations && "transition-all duration-300 ease-out"
                    )
                }}
                selectionMode="single"
                variant="flat"
                onSelectionChange={handleSelectionChange}
                onKeyDown={handleKeyDown}
                {...rawProps}
            >
                {isSidebarGroupItems(items)
                    ? items.map(({ key, title, items: groupItems, ...sectionProps }) => (
                        <ListboxSection
                            key={key}
                            title={isCollapsed ? "" : title}
                            showDivider={!isCollapsed}
                            classNames={{
                                base: clsx(
                                    "w-full",
                                    enableAnimations && "transition-all duration-300 ease-out",
                                    isCollapsed ? "p-0 max-w-11 mx-auto" : "px-0 py-2"
                                ),
                                group: clsx(
                                    "flex flex-col gap-1",
                                    isCollapsed && "items-center"
                                ),
                                heading: clsx(
                                    "text-xs font-semibold text-default-600 uppercase tracking-wider",
                                    "px-3 py-2 mb-1",
                                    enableAnimations && "transition-all duration-300 ease-out",
                                    isCollapsed ? "hidden" : "block"
                                ),
                                divider: clsx(
                                    "mt-2 mb-2",
                                    isCollapsed ? "hidden" : "block"
                                ),
                            }}
                            aria-label={title}
                            {...sectionProps}
                        >
                            {listItems(groupItems)}
                        </ListboxSection>
                    ))
                    : listItems(items)
                }
            </Listbox>
            {typeof bottomContent === "function" ? bottomContent(isCollapsed) : bottomContent}
        </ScrollShadow>
    )
});

SidebarMenus.displayName = "SidebarMenus";
